import React, { useState, useCallback } from 'react';
import { 
  Grid, 
  Card, 
  Text, 
  Badge, 
  Button, 
  Group, 
  Stack,
  Avatar,
  Tooltip,
  ActionIcon,
  Modal,
  ScrollArea,
  Divider,
  Center
} from '@mantine/core';
import { 
  IconUser, 
  IconUserCheck, 
  IconUserX, 
  IconUpload,
  IconExternalLink,
  IconCalendar,
  IconClock,
  IconEye,
  IconChartBar
} from '@tabler/icons-react';
import moment from 'moment';
import { useCachedAvatar } from '../../hooks/useCachedAvatar';
import { getAvatarFallbackText } from '../../utils/publicAvatarUtils';

export const UserListGrid = ({ 
  users, 
  onUserSelect, 
  selectedUser, 
  loading 
}) => {
  const [previewModal, setPreviewModal] = useState(false);
  const [previewUser, setPreviewUser] = useState(null);

  const handleUserClick = (user) => {
    onUserSelect(user);
  };

  const openPreview = (user, event) => {
    event.stopPropagation();
    setPreviewUser(user);
    setPreviewModal(true);
  };

  const getLastVisitText = (user) => {
    if (!user.visitLog || user.visitLog.length === 0) {
      return '방문 기록 없음';
    }
    
    const lastVisit = user.visitLog[user.visitLog.length - 1];
    const visitTime = moment(lastVisit);
    const now = moment();
    
    if (now.diff(visitTime, 'days') === 0) {
      return `오늘 ${visitTime.format('HH:mm')}`;
    } else if (now.diff(visitTime, 'days') === 1) {
      return `어제 ${visitTime.format('HH:mm')}`;
    } else {
      return visitTime.format('MM/DD HH:mm');
    }
  };

  const getSignUpText = (user) => {
    if (!user.signUpDate) return '가입일 알 수 없음';
    
    const signUpTime = moment(user.signUpDate);
    return signUpTime.format('YYYY년 MM월 DD일');
  };

  if (loading) {
    return (
      <Center style={{ height: '200px' }}>
        <Stack align="center" spacing="md">
          <Text>사용자 목록을 불러오는 중...</Text>
        </Stack>
      </Center>
    );
  }

  return (
    <>
      <Grid gutter="md">
        {users.map((user) => (
          <Grid.Col key={user.id} span={4}>
            <Card 
              shadow="sm" 
              padding="lg" 
              radius="md" 
              withBorder
              style={{ 
                cursor: 'pointer',
                borderColor: selectedUser?.id === user.id ? 'var(--mantine-color-blue-6)' : undefined
              }}
              onClick={(e) => openPreview(user, e)}
            >
              <Stack spacing="sm">
                {/* 아바타 및 사용자 정보 */}
                <Group>
                  <UserAvatar 
                    userID={user.id}
                    fallbackUrl={user.profile?.profileImage}
                    username={user.profile?.username}
                  />
                  <Stack spacing={4}>
                    <Text size="sm" weight={500} lineClamp={1}>
                      {user.profile?.name || 'Unknown'}
                    </Text>
                    <Text size="xs" color="dimmed" lineClamp={1}>
                      @{user.profile?.username || 'username'}
                    </Text>
                  </Stack>
                </Group>

                {/* 아바타 상태 */}
                <Badge 
                  color={user.hasAvatar ? "green" : "red"} 
                  variant="light"
                  size="sm"
                >
                  {user.hasAvatar ? "아바타 있음" : "아바타 없음"}
                </Badge>
                
                {/* 컬렉션 정보 추가 */}
                <Group spacing="xs">
                  <IconCalendar size={12} />
                  <Text size="xs" color="dimmed">
                    컬렉션 {user.collectionCount || 0}개
                  </Text>
                </Group>
                
                <Group spacing="xs">
                  <IconClock size={12} />
                  <Text size="xs" color="dimmed">
                    {getLastVisitText(user)}
                  </Text>
                </Group>
                
                <Group spacing="xs">
                  <IconEye size={12} />
                  <Text size="xs" color="dimmed">
                    방문 {user.visitLog?.length || 0}회
                  </Text>
                </Group>
              </Stack>

              <Group position="apart" mt="md">
                <Button 
                  variant="light" 
                  size="xs" 
                  leftIcon={<IconUpload size={14} />}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleUserClick(user);
                  }}
                  disabled={user.hasAvatar}
                >
                  {user.hasAvatar ? '업로드됨' : '업로드'}
                </Button>
                
                <Group spacing="xs">
                  <Tooltip label="사용자 스레드 페이지 보기">
                    <ActionIcon 
                      size="sm" 
                      variant="subtle"
                      component="a"
                      href={`/dashboard?user=${user.id}`}
                      target="_blank"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <IconChartBar size={16} />
                    </ActionIcon>
                  </Tooltip>
                  
                  <Tooltip label="Threads 프로필 보기">
                    <ActionIcon 
                      size="sm" 
                      variant="subtle"
                      component="a"
                      href={`https://threads.net/@${user.profile?.username}`}
                      target="_blank"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <IconExternalLink size={16} />
                    </ActionIcon>
                  </Tooltip>
                </Group>
              </Group>
            </Card>
          </Grid.Col>
        ))}
      </Grid>

      {/* 사용자 상세 모달 */}
      <Modal
        opened={previewModal}
        onClose={() => setPreviewModal(false)}
        title="사용자 상세 정보"
        size="md"
      >
        {previewUser && (
          <ScrollArea h={400}>
            <Stack spacing="md">
              <Group>
                <UserAvatar 
                  userID={previewUser.id}
                  fallbackUrl={previewUser.profile?.profileImage}
                  username={previewUser.profile?.username}
                  size="xl"
                />
                <Stack spacing="xs">
                  <Text weight={600} size="lg">
                    {previewUser.profile?.name}
                  </Text>
                  <Text size="sm" color="dimmed">
                    @{previewUser.profile?.username}
                  </Text>
                  <Badge 
                    color={previewUser.hasAvatar ? "green" : "red"} 
                    variant="light"
                  >
                    {previewUser.hasAvatar ? "아바타 있음" : "아바타 없음"}
                  </Badge>
                </Stack>
              </Group>
              
              <Divider />
              
              <Stack spacing="xs">
                <Group>
                  <IconCalendar size={16} />
                  <Text size="sm">
                    컬렉션 수: {previewUser.collectionCount || 0}개
                  </Text>
                </Group>
                
                <Group>
                  <IconClock size={16} />
                  <Text size="sm">
                    최근 방문: {getLastVisitText(previewUser)}
                  </Text>
                </Group>
                
                <Group>
                  <IconEye size={16} />
                  <Text size="sm">
                    총 방문: {previewUser.visitLog?.length || 0}회
                  </Text>
                </Group>
                
                {/* 컬렉션 목록 표시 */}
                {previewUser.collections && previewUser.collections.length > 0 && (
                  <>
                    <Divider />
                    <Text size="sm" weight={500}>작성한 컬렉션:</Text>
                    <Stack spacing="xs">
                      {previewUser.collections.slice(0, 3).map(collection => (
                        <Text key={collection.id} size="xs" color="dimmed">
                          • {collection.title}
                        </Text>
                      ))}
                      {previewUser.collections.length > 3 && (
                        <Text size="xs" color="dimmed">
                          ... 외 {previewUser.collections.length - 3}개
                        </Text>
                      )}
                    </Stack>
                  </>
                )}
              </Stack>
              
              {/* 액션 버튼들 */}
              <Divider />
              
              <Group grow>
                <Button
                  variant="light"
                  leftIcon={<IconChartBar size={16} />}
                  component="a"
                  href={`/dashboard?user=${previewUser.id}`}
                  target="_blank"
                  fullWidth
                >
                  스레드 페이지 보기
                </Button>
                
                <Button
                  variant="light"
                  leftIcon={<IconExternalLink size={16} />}
                  component="a"
                  href={`https://threads.net/@${previewUser.profile?.username}`}
                  target="_blank"
                  fullWidth
                >
                  Threads 프로필
                </Button>
              </Group>
              
              <Button
                variant="filled"
                leftIcon={<IconUpload size={16} />}
                onClick={() => {
                  handleUserClick(previewUser);
                  setPreviewModal(false);
                }}
                disabled={previewUser.hasAvatar}
                fullWidth
              >
                {previewUser.hasAvatar ? '이미 업로드됨' : '아바타 업로드'}
              </Button>
            </Stack>
          </ScrollArea>
        )}
      </Modal>
    </>
  );
};

// 아바타 컴포넌트 (AllCollection.js와 동일한 방식)
const UserAvatar = ({ userID, fallbackUrl, username, size = "md" }) => {
  const { imageSrc, loading, error } = useCachedAvatar(userID, fallbackUrl);
  
  // 사이즈 설정
  let avatarSize;
  switch (size) {
    case "small":
      avatarSize = 24; break;
    case "big":
      avatarSize = 48; break;
    case "xl":
      avatarSize = 64; break;
    default:
      avatarSize = 32; break;
  }

  const handleImageError = (event) => {
    console.warn(`⚠️ Avatar image error for user ${username}`);
    event.target.src = "";
    event.target.alt = getAvatarFallbackText(username);
  };

  return (
    <Avatar
      src={imageSrc}
      size={avatarSize}
      onError={handleImageError}
      radius="xl"
      fallback={<IconUser size={avatarSize * 0.6} />}
    >
      {getAvatarFallbackText(username)}
    </Avatar>
  );
};
